import type { GlossaryEntry } from 'docs/glossary'

/**
 * Does this entry have anything to show BEYOND the tooltip that spawned it?
 *
 * The load-bearing predicate, and the reason `TermOverlay` is not simply wired to every
 * tooltip. The tooltip body ALREADY IS `short` (§44) — so an overlay whose only content is
 * `short` is a click that buys the reader nothing, and worse, it takes a reader who wanted
 * one sentence and makes them dismiss a dialog to get back to what they were doing.
 *
 * `long` is the obvious extra, but it is NOT the only one, and gating on it alone would be
 * wrong twice over: it is absent from most entries today (content is still being written),
 * and it will never reach 100% — some terms are genuinely one sentence and nothing more.
 * `aliases`, `formula`, `seeAlso` and `docs` are each material the tooltip cannot show, so
 * any one of them is enough to make the overlay a net gain.
 *
 * When this returns false the tooltip footer keeps NAVIGATING to the glossary page, which is
 * the honest outcome: there is no expanded view worth opening, so don't pretend there is.
 * That path is the pre-existing behaviour, unchanged.
 *
 * `aliases` is deliberately included. "also: certificates, digital certificate" is exactly
 * the fact that resolves "is the thing I just read about the thing I am looking at?", which
 * is a different question from the one `short` answers.
 *
 * ── Why this lives in its own file ──────────────────────────────────────────────────
 *
 * It was originally exported from `TermOverlay.tsx`, and Vite said so out loud: "Could not
 * Fast Refresh (hasExpandedEntry export is incompatible)". A module that exports both a
 * component and a non-component opts out of Fast Refresh, so every edit to the overlay
 * triggered a FULL PAGE RELOAD of the platform — which, mid-verification, looks exactly like
 * the app crashing (blank screen, empty `document.body.innerText`) and cost a detour
 * diagnosing it. Splitting the predicate out restores component-level HMR, and it also lets
 * a caller ask the question without importing the dialog.
 */
export const hasExpandedEntry = (entry: GlossaryEntry): boolean =>
  Boolean(
    entry.long ||
    entry.formula ||
    entry.aliases?.length ||
    entry.seeAlso?.length ||
    entry.docs?.length,
  )
